import { Anima } from "kido/anima";
import { ResizeHub } from "kido/resize";
import { clamp, damp, lerp, queryAll, Ease } from "kido/utils";

import { App } from "@app/context";
import type Scene from "@app/gpu/scene";
import { BasePage } from "@app/primitives/base-page";

export class ProjectPage extends BasePage {
  private scene: Scene | null = null;
  private strip: HTMLElement | null = null;

  // Horizontal scroll state
  private _scrollTarget = 0;
  private _scrollCurrent = 0;
  private _scrollMax = 0;
  private _stripOffset = 0;
  private _onWheel: ((e: WheelEvent) => void) | null = null;

  // Arrow key scroll
  private _onKeyDown: ((e: KeyboardEvent) => void) | null = null;

  // Touch drag scroll (iPad / touch devices at horizontal-layout widths)
  private _onTouchStart: ((e: TouchEvent) => void) | null = null;
  private _onTouchMove: ((e: TouchEvent) => void) | null = null;
  private _onTouchEnd: ((e: TouchEvent) => void) | null = null;
  private _touchLastX = 0;
  private _touchVel = 0;

  // Whether the horizontal strip layout is active (viewport >= CSS `md`).
  // Tracked live via matchMedia so JS behavior always agrees with the CSS
  // breakpoint, including window resizes across it.
  private _horizontal = false;
  private _mql: MediaQueryList | null = null;
  private _onMqlChange: (() => void) | null = null;
  // Set while the page is live (between in() and out()) — gates the strip
  // driver so a breakpoint flip before intro doesn't start it early.
  private _inActive = false;

  // Info panel state
  private _infoOpen = false;
  private _infoBar: HTMLElement | null = null;
  private _infoHeader: HTMLElement | null = null;
  private _infoToggle: HTMLElement | null = null;
  private _infoScrim: HTMLElement | null = null;
  private _onInfoClick: (() => void) | null = null;
  private _onScrimClick: (() => void) | null = null;

  // Back-to-beginning button
  private _backBtn: HTMLElement | null = null;
  private _onBackClick: (() => void) | null = null;

  // Track whether we came from projects grid (for morph animation)
  private _fromProjects = false;

  // Resize handler
  private _resizeId: symbol | null = null;
  private _rafId = 0;

  constructor() {
    super();
  }

  async init(container: Element | null): Promise<void> {
    if (!container) return;
    await super.init(container);

    const url = App.route.new.url ?? "";

    this.strip = (container as HTMLElement).querySelector(".pj-strip");

    // Horizontal-layout widths (CSS `md` and up): wheel, arrow-key, and touch
    // drag scroll. Below that the CSS stacks vertically and native scroll runs.
    // Must mirror the CSS breakpoint exactly — innerWidth disagrees with media
    // queries when a scrollbar is present, and a resize across the breakpoint
    // flips the CSS layout without re-running init.
    this._mql = window.matchMedia("(min-width: 1000px)");
    this._horizontal = this._mql.matches;
    if (this._horizontal) this._enterHorizontal();

    this._onMqlChange = () => {
      const horizontal = this._mql?.matches ?? false;
      if (horizontal === this._horizontal) return;
      this._horizontal = horizontal;
      if (horizontal) {
        this._enterHorizontal();
        this._startDriver();
      } else {
        this._exitHorizontal();
      }
    };
    this._mql.addEventListener("change", this._onMqlChange);

    // Info panel
    this._infoBar = (container as HTMLElement).querySelector(".pj-info");
    this._infoHeader = (container as HTMLElement).querySelector(".pj-info_h");
    this._infoToggle = (container as HTMLElement).querySelector(
      ".pj-info_toggle"
    );
    this._onInfoClick = () => this._toggleInfo();
    this._infoToggle?.addEventListener("click", this._onInfoClick);
    this._infoScrim = (container as HTMLElement).querySelector(
      ".pj-info_scrim"
    );
    this._onScrimClick = () => {
      if (this._infoOpen) this._toggleInfo();
    };
    this._infoScrim?.addEventListener("click", this._onScrimClick);

    // Back-to-beginning button
    this._backBtn = (container as HTMLElement).querySelector(".pj-info_back");
    this._onBackClick = () => {
      this._scrollTarget = 0;
      App.gpu?.requestExtraFrames?.();
    };
    this._backBtn?.addEventListener("click", this._onBackClick);

    // Recalculate info bar closed position on resize
    this._resizeId = ResizeHub.add(() => this._resizeInfoBar());

    // Get the shared /projects scene
    const sceneKey = "/projects";
    this.scene = (App.gpu?.scenes as Record<string, Scene>)?.[sceneKey] ?? null;

    if (this.scene) {
      this.scene.restorePlanes();
      const ts = App.transitState;
      this.scene.injectProjectMedia(url, ts?.planeId);
    }

    // Check if coming from projects grid with cover bounds for morph
    const ts = App.transitState;
    this._fromProjects = !!(
      ts?.direction === "to-project" &&
      ts.coverBounds &&
      this.scene &&
      App.gpu
    );

    if (this._fromProjects && App.gpu) {
      App.gpu.freezeBounds?.(true);

      // Position cover plane at its old grid bounds
      const coverPlane = this.scene!.planes[0];
      if (coverPlane && ts!.coverBounds) {
        const cb = ts!.coverBounds;
        if (!coverPlane.boundsPx) {
          coverPlane.boundsPx = { x: 0, y: 0, w: 0, h: 0, z: 1 };
        }
        coverPlane.boundsPx.x = cb.x;
        coverPlane.boundsPx.y = cb.y;
        coverPlane.boundsPx.w = cb.w;
        coverPlane.boundsPx.h = cb.h;
      }

      // Hide all non-cover planes
      const scenePlanes = this.scene!.planes;
      for (let i = 1; i < scenePlanes.length; i++) {
        const p = scenePlanes[i];
        if (p) p.opacity = 0;
      }

      // Hide all non-cover items (media blocks, text, play buttons)
      if (this.strip) {
        const items = queryAll(this.strip, ".pj-item");
        for (const item of items) {
          if (!(item as HTMLElement).classList.contains("pj-cover")) {
            (item as HTMLElement).style.opacity = "0";
          }
        }
        // Also hide text/play inside the cover item
        const coverTexts = queryAll(
          this.strip,
          ".pj-cover .pj-text, .pj-cover .pj-play"
        );
        for (const el of coverTexts) {
          (el as HTMLElement).style.opacity = "0";
        }
      }

      // Hide info bar fully (below viewport)
      if (this._infoBar) {
        this._infoBar.style.opacity = "0";
        this._infoBar.style.transform = `translateY(${this._infoBar.offsetHeight}px)`;
      }
    } else {
      // Normal init — set info bar to header-visible position
      if (this._infoBar) {
        const offset = this._getInfoClosedOffset();
        this._infoBar.style.transform = `translateY(${offset}px)`;
      }
    }

    if (!this._fromProjects) {
      App.gpu?.requestBoundsUpdate?.();
      App.gpu?.requestRedraw?.();
    }
  }

  private _loop(): void {
    if (!this.strip) return;

    const max = Math.max(
      0,
      this.strip.scrollWidth - window.innerWidth + this._stripOffset
    );
    if (max !== this._scrollMax) {
      this._scrollMax = max;
      this._scrollTarget = clamp(this._scrollTarget, 0, max);
    }

    this._scrollCurrent = damp(this._scrollCurrent, this._scrollTarget, 0.18);
    this.strip.style.transform = `translate3d(${-this._scrollCurrent}px, 0, 0)`;

    if (Math.abs(this._scrollCurrent - this._scrollTarget) > 0.5) {
      App.gpu?.requestExtraFrames?.();
    }
  }

  private _getInfoClosedOffset(): number {
    if (!this._infoBar) return 0;
    const barH = this._infoBar.offsetHeight;
    const headerH = this._infoHeader?.offsetHeight ?? 53;
    return barH - headerH;
  }

  private _resizeInfoBar(): void {
    if (!this._infoBar || this._infoOpen) return;
    this._infoBar.style.transform = `translateY(${this._getInfoClosedOffset()}px)`;
  }

  private _startRafLoop(): void {
    const tick = () => {
      this._loop();
      this._rafId = requestAnimationFrame(tick);
    };
    this._rafId = requestAnimationFrame(tick);
  }

  private _stopRafLoop(): void {
    if (this._rafId) {
      cancelAnimationFrame(this._rafId);
      this._rafId = 0;
    }
  }

  private _toggleInfo(): void {
    this._infoOpen = !this._infoOpen;
    const open = this._infoOpen;
    const bar = this._infoBar;
    if (!bar) return;

    // Toggle the + / − icon
    const icon = bar.querySelector(".pj-info_icon");
    if (icon) {
      if (open) icon.classList.add("is-open");
      else icon.classList.remove("is-open");
    }

    const ease = Ease.o1 ?? ((t: number) => t);
    const DUR = 800;
    const closedOffset = this._getInfoClosedOffset();
    const openOffset = 0;

    const nav = document.getElementById("nav");

    if (open) {
      this._infoScrim?.classList.add("is-active");
      if (nav) nav.style.mixBlendMode = "normal";

      if (this._onWheel) {
        window.removeEventListener("wheel", this._onWheel);
      }

      const start = performance.now();
      const tick = () => {
        const elapsed = performance.now() - start;
        const t = ease(clamp(elapsed / DUR, 0, 1));
        bar.style.transform = `translateY(${lerp(closedOffset, openOffset, t)}px)`;
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    } else {
      this._infoScrim?.classList.remove("is-active");
      if (nav) nav.style.mixBlendMode = "";
      bar.scrollTop = 0;

      if (this._onWheel) {
        window.addEventListener("wheel", this._onWheel, { passive: false });
      }

      const start = performance.now();
      const tick = () => {
        const t = ease(clamp((performance.now() - start) / DUR, 0, 1));
        bar.style.transform = `translateY(${lerp(openOffset, closedOffset, t)}px)`;
        if (t < 1) requestAnimationFrame(tick);
      };
      requestAnimationFrame(tick);
    }
  }

  async in(): Promise<void> {
    const ts = App.transitState;
    App.transitState = null;
    this._inActive = true;

    if (!App.gpu) {
      // No GPU (mobile, tablet, or no-GPU desktop): set info bar to
      // header-visible position; drive the strip with rAF when horizontal
      if (this._infoBar) {
        const offset = this._getInfoClosedOffset();
        this._infoBar.style.transform = `translateY(${offset}px)`;
      }
      this._startDriver();
      return;
    }

    if (this._fromProjects && ts?.coverBounds && this.scene) {
      const coverPlane = this.scene.planes[0];
      const coverSlot = this.scene.domSlots[0] as HTMLElement | null;
      const scenePlanes = this.scene.planes;
      const sceneSlots = this.scene.domSlots;

      const morphDuration = 800;
      // Delay stagger + info bar until morph is 90% complete
      const staggerDelay = Math.round(morphDuration * 0.9);

      // 1. Cover morph
      if (coverPlane && coverSlot) {
        const targetRect = coverSlot.getBoundingClientRect();
        const startBounds = ts.coverBounds;

        new Anima({
          d: morphDuration,
          e: "ioW",
          u: (v) => {
            const t = v.prE;
            if (coverPlane.boundsPx) {
              coverPlane.boundsPx.x = lerp(startBounds.x, targetRect.x, t);
              coverPlane.boundsPx.y = lerp(startBounds.y, targetRect.y, t);
              coverPlane.boundsPx.w = lerp(startBounds.w, targetRect.width, t);
              coverPlane.boundsPx.h = lerp(startBounds.h, targetRect.height, t);
            }
            App.gpu?.requestRedraw?.();
          },
          cb: () => {
            if (coverPlane.boundsPx) {
              coverPlane.boundsPx.x = targetRect.x;
              coverPlane.boundsPx.y = targetRect.y;
              coverPlane.boundsPx.w = targetRect.width;
              coverPlane.boundsPx.h = targetRect.height;
            }
            App.gpu?.freezeBounds?.(false);
          },
        }).play();
      }

      // Fade in cover text/play button at morph ~90%
      if (this.strip) {
        const coverEls = queryAll(
          this.strip,
          ".pj-cover .pj-text, .pj-cover .pj-play"
        );
        for (const el of coverEls) {
          new Anima({
            el: el as HTMLElement,
            p: { opacity: [0, 1] },
            d: 600,
            e: "oQ",
            de: staggerDelay,
          }).play();
        }
      }

      // 2. Stagger in non-cover items + planes (delayed until morph ~90%)
      const items = this.strip
        ? queryAll(this.strip, ".pj-item:not(.pj-cover)")
        : [];

      const baseStagger = 80;
      const staggerDecay = 0.65;
      const getDelay = (s: number): number => {
        if (s <= 0) return 0;
        return (
          (baseStagger * (1 - Math.pow(staggerDecay, s))) / (1 - staggerDecay)
        );
      };

      const staggerDone = new Promise<void>((resolve) => {
        if (!items.length) {
          resolve();
          return;
        }
        let completed = 0;

        for (let s = 0; s < items.length; s++) {
          const item = items[s] as HTMLElement;
          const delay = staggerDelay + getDelay(s);

          // Find GPU planes within this item and fade them in
          const figures = queryAll(item, ".g__l");
          for (const fig of figures) {
            const planeId = (fig as HTMLElement).getAttribute("data-plane");
            if (!planeId) continue;
            for (let p = 0; p < sceneSlots.length; p++) {
              const slot = sceneSlots[p] as HTMLElement | null;
              if (
                slot?.getAttribute("data-plane") === planeId &&
                scenePlanes[p]
              ) {
                const plane = scenePlanes[p]!;
                new Anima({
                  d: 800,
                  e: "oQ",
                  de: delay,
                  u: (v) => {
                    plane.opacity = v.prE;
                    App.gpu?.requestRedraw?.();
                  },
                  cb: () => {
                    plane.opacity = 1;
                  },
                }).play();
              }
            }
          }

          // Fade in DOM item
          new Anima({
            el: item,
            p: { opacity: [0, 1] },
            d: 800,
            e: "oQ",
            de: delay,
            cb: () => {
              completed++;
              if (completed >= items.length) resolve();
            },
          }).play();
        }
      });

      // 3. Info bar: slide up (delayed until morph ~90%)
      if (this._infoBar) {
        const barH = this._infoBar.offsetHeight;
        const targetOffset = this._getInfoClosedOffset();

        new Anima({
          el: this._infoBar,
          p: { opacity: [0, 1] },
          d: 800,
          e: "oQ",
          de: staggerDelay,
        }).play();

        const infoBar = this._infoBar;
        const startOffset = barH;
        const ease = Ease.oQ ?? Ease.o1 ?? ((t: number) => t);
        setTimeout(() => {
          const start = performance.now();
          const tick = () => {
            const t = ease(clamp((performance.now() - start) / 600, 0, 1));
            infoBar.style.transform = `translateY(${lerp(startOffset, targetOffset, t)}px)`;
            if (t < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        }, staggerDelay);
      }

      await staggerDone;
    } else {
      // Normal entry (not from projects)
      if (this._infoBar) {
        const offset = this._getInfoClosedOffset();
        this._infoBar.style.transform = `translateY(${offset}px)`;
      }
      App.gpu.freezeBounds?.(false);
    }

    this._startDriver();
  }

  async out(): Promise<void> {
    // Reset nav blend mode if info popup was open
    if (this._infoOpen) {
      const nav = document.getElementById("nav");
      if (nav) nav.style.mixBlendMode = "";
      this._infoScrim?.classList.remove("is-active");
    }

    this._inActive = false;

    if (this._mql && this._onMqlChange) {
      this._mql.removeEventListener("change", this._onMqlChange);
    }
    this._mql = null;
    this._onMqlChange = null;

    if (this._horizontal) this._exitHorizontal();

    this._destroy();
  }

  /** Take over scrolling for the horizontal strip layout. */
  private _enterHorizontal(): void {
    App.scroller?.pause?.();
    document.documentElement.style.overflow = "hidden";
    window.scrollTo(0, 0);

    if (!this.strip) return;
    this.strip.style.transform = "";
    this._stripOffset = this.strip.getBoundingClientRect().left;
    this._scrollMax = Math.max(
      0,
      this.strip.scrollWidth - window.innerWidth + this._stripOffset
    );
    this._scrollTarget = 0;
    this._scrollCurrent = 0;
    this._addScrollListeners();
  }

  /** Hand scrolling back to the native/vertical flow. */
  private _exitHorizontal(): void {
    this._removeScrollListeners();
    this._stopDriver();
    if (this.strip) this.strip.style.transform = "";
    this._scrollTarget = 0;
    this._scrollCurrent = 0;
    App.scroller?.resume?.();
    document.documentElement.style.overflow = "";
  }

  /** Start whatever loop moves the strip: GPU pre-render or plain rAF. */
  private _startDriver(): void {
    if (!this._horizontal || !this._inActive) return;
    if (App.gpu) {
      App.gpu.setPreRender?.(() => this._loop());
    } else {
      this._startRafLoop();
    }
  }

  private _stopDriver(): void {
    App.gpu?.setPreRender?.(null);
    this._stopRafLoop();
  }

  private _addScrollListeners(): void {
    if (this._onWheel) return; // already attached

    this._onWheel = (e: WheelEvent) => {
      if (e.ctrlKey) return;
      e.preventDefault();
      let delta = e.deltaX !== 0 ? e.deltaX : e.deltaY;
      if (e.deltaMode === 1) delta *= 40;
      if (e.deltaMode === 2) delta *= 800;
      this._scrollTarget = clamp(this._scrollTarget + delta, 0, this._scrollMax);
      App.gpu?.requestExtraFrames?.();
    };
    window.addEventListener("wheel", this._onWheel, { passive: false });

    const ARROW_STEP = 200;
    this._onKeyDown = (e: KeyboardEvent) => {
      if (this._infoOpen) return;
      const key = e.key;
      if (key === "ArrowRight" || key === "ArrowUp") {
        e.preventDefault();
        this._scrollTarget = clamp(
          this._scrollTarget + ARROW_STEP,
          0,
          this._scrollMax
        );
        App.gpu?.requestExtraFrames?.();
      } else if (key === "ArrowLeft" || key === "ArrowDown") {
        e.preventDefault();
        this._scrollTarget = clamp(
          this._scrollTarget - ARROW_STEP,
          0,
          this._scrollMax
        );
        App.gpu?.requestExtraFrames?.();
      }
    };
    window.addEventListener("keydown", this._onKeyDown);

    // Touch drag: finger drag moves the strip, release adds flick momentum
    this._onTouchStart = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || this._infoOpen) return;
      this._touchLastX = t.clientX;
      this._touchVel = 0;
      // Landing a finger stops any in-flight glide
      this._scrollTarget = clamp(this._scrollCurrent, 0, this._scrollMax);
    };
    this._onTouchMove = (e: TouchEvent) => {
      const t = e.touches[0];
      if (!t || this._infoOpen) return;
      e.preventDefault();
      const dx = this._touchLastX - t.clientX;
      this._touchLastX = t.clientX;
      this._touchVel = dx;
      this._scrollTarget = clamp(this._scrollTarget + dx, 0, this._scrollMax);
      App.gpu?.requestExtraFrames?.();
    };
    this._onTouchEnd = () => {
      if (this._infoOpen) return;
      this._scrollTarget = clamp(
        this._scrollTarget + this._touchVel * 15,
        0,
        this._scrollMax
      );
      this._touchVel = 0;
      App.gpu?.requestExtraFrames?.();
    };
    window.addEventListener("touchstart", this._onTouchStart, {
      passive: true,
    });
    window.addEventListener("touchmove", this._onTouchMove, {
      passive: false,
    });
    window.addEventListener("touchend", this._onTouchEnd, {
      passive: true,
    });
  }

  private _removeScrollListeners(): void {
    if (this._onWheel) {
      window.removeEventListener("wheel", this._onWheel);
      this._onWheel = null;
    }
    if (this._onKeyDown) {
      window.removeEventListener("keydown", this._onKeyDown);
      this._onKeyDown = null;
    }
    if (this._onTouchStart) {
      window.removeEventListener("touchstart", this._onTouchStart);
      this._onTouchStart = null;
    }
    if (this._onTouchMove) {
      window.removeEventListener("touchmove", this._onTouchMove);
      this._onTouchMove = null;
    }
    if (this._onTouchEnd) {
      window.removeEventListener("touchend", this._onTouchEnd);
      this._onTouchEnd = null;
    }
  }

  private _destroy(): void {
    this.strip = null;

    if (this._resizeId) {
      ResizeHub.remove(this._resizeId);
      this._resizeId = null;
    }

    if (this._infoToggle && this._onInfoClick) {
      this._infoToggle.removeEventListener("click", this._onInfoClick);
    }
    if (this._infoScrim && this._onScrimClick) {
      this._infoScrim.removeEventListener("click", this._onScrimClick);
    }
    if (this._backBtn && this._onBackClick) {
      this._backBtn.removeEventListener("click", this._onBackClick);
    }
    this._infoBar = null;
    this._infoToggle = null;
    this._infoScrim = null;
    this._onInfoClick = null;
    this._onScrimClick = null;
    this._backBtn = null;
    this._onBackClick = null;
    this._onKeyDown = null;
    this._onTouchStart = null;
    this._onTouchMove = null;
    this._onTouchEnd = null;
    this._touchVel = 0;
    this._horizontal = false;
    this._infoOpen = false;
    this._fromProjects = false;

    this.scene = null;
    this._scrollTarget = 0;
    this._scrollCurrent = 0;
    this._scrollMax = 0;
    this._stripOffset = 0;
  }
}
